import { ErrorHandler } from '@angular/core';

/** Reloads once on stale-chunk load failures after a deploy; delegates everything else. */
const CHUNK_LOAD_ERROR_PATTERNS = [
	'failed to fetch dynamically imported module',
	'error loading dynamically imported module',
	'importing a module script failed',
	'loading chunk',
	'loading css chunk',
];

const RELOAD_GUARD_STORAGE_KEY = 'spoken:chunk-load-reload-at';
const RELOAD_COOLDOWN_MS = 15_000;

export class ChunkLoadErrorHandler implements ErrorHandler {
	constructor(private readonly _delegateErrorHandler: ErrorHandler) {}

	handleError(error: unknown): void {
		// Reload only if we can also record the guard marker; otherwise skip it to
		// avoid a loop. Every branch is safe so this handler can never itself throw.
		if (this._isChunkLoadError(error) && this._canReloadNow() && this._recordReloadMarker()) {
			window.location.reload();
			return;
		}
		this._delegateErrorHandler.handleError(error);
	}

	private _isChunkLoadError(error: unknown): boolean {
		const errorName = this._readStringProperty(error, 'name').toLowerCase();
		if (errorName === 'chunkloaderror') {
			return true;
		}
		const errorMessage = this._extractMessage(error).toLowerCase();
		return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => errorMessage.includes(pattern));
	}

	private _extractMessage(error: unknown): string {
		if (typeof error === 'string') {
			return error;
		}
		if (error instanceof Error) {
			return `${error.name}: ${error.message}`;
		}
		const nestedMessage = this._readStringProperty(error, 'message');
		return nestedMessage || String(error ?? '');
	}

	private _readStringProperty(value: unknown, propertyName: string): string {
		if (value !== null && typeof value === 'object' && propertyName in value) {
			try {
				const propertyValue: unknown = Reflect.get(value, propertyName);
				return typeof propertyValue === 'string' ? propertyValue : '';
			} catch {
				// e.g. a throwing getter or exotic proxy — treat as no message.
				return '';
			}
		}
		return '';
	}

	private _canReloadNow(): boolean {
		// Guard against a reload loop if the freshly fetched index also fails.
		try {
			const lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_GUARD_STORAGE_KEY) ?? '0');
			return !Number.isFinite(lastReloadAt) || Date.now() - lastReloadAt > RELOAD_COOLDOWN_MS;
		} catch {
			return false;
		}
	}

	private _recordReloadMarker(): boolean {
		try {
			window.sessionStorage.setItem(RELOAD_GUARD_STORAGE_KEY, String(Date.now()));
			return true;
		} catch {
			// sessionStorage blocked / full — don't reload without a working guard.
			return false;
		}
	}
}
